import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import { useSearchParams } from 'react-router-dom';
import { EmailForm } from './EmailForm';
import { AuthErrorAlert } from './AuthErrorAlert';
import { ErrorCodes, withErrorCode } from '../../api/errorCodes';
import { hasActiveSession, currentUrl } from '../../utils/apiClient';
import { isSafeInternalPath } from '../../utils/authRedirect';
import { navigateTo } from '../../utils/navigation';
import { SignInCard } from './SignInCard';

const useStyles = makeStyles(theme => ({
  title: {
    marginBottom: theme.spacing(3),
    textAlign: 'center' as const,
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
}));

/**
 * Product login screen - collects the user's email then redirects to the
 * backend's Keycloak-backed /auth/login. Keycloak handles identity-first
 * routing (Organizations) and the email OTP challenge (mesutpiskin SPI).
 */
export function EmailSignInPage() {
  const { t } = useTranslation('auth');
  const classes = useStyles();
  const [checkingSession, setCheckingSession] = useState(true);
  const [searchParams] = useSearchParams();
  const [showSessionExpiredAlert, setShowSessionExpiredAlert] = useState(false);
  const rawReturnTo = searchParams.get('return_to');
  const returnTo = isSafeInternalPath(rawReturnTo) ? rawReturnTo : undefined;

  useEffect(() => {
    let cancelled = false;
    hasActiveSession().then(active => {
      if (cancelled) return;
      if (active) {
        navigateTo('/home');
        return;
      }
      setCheckingSession(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (searchParams.get('reason') === 'session-expired') {
      setShowSessionExpiredAlert(true);
      const timer = setTimeout(() => {
        setShowSessionExpiredAlert(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [searchParams]);

  const handleSubmit = useCallback(
    (email: string) => {
      // Absolute: the callback completes on the backend's host, so a bare path
      // would resolve there rather than in the app.
      const params = new URLSearchParams({ login_hint: email });
      params.set(
        'return_to',
        returnTo ? `${window.location.origin}${returnTo}` : currentUrl(),
      );
      navigateTo(`/auth/login?${params.toString()}`);
    },
    [returnTo],
  );

  if (checkingSession) {
    return null;
  }

  return (
    <SignInCard>
      {showSessionExpiredAlert && (
        <Alert
          severity="warning"
          onClose={() => setShowSessionExpiredAlert(false)}
          className={classes.alert}
        >
          {withErrorCode(t('sessionExpired'), ErrorCodes.sessionExpired)}
        </Alert>
      )}
      <AuthErrorAlert className={classes.alert} />
      <Typography variant="h5" className={classes.title}>
        {t('title')}
      </Typography>
      <EmailForm loading={false} onSubmit={handleSubmit} />
    </SignInCard>
  );
}
